import type { AircraftFlightSummaries, FlightHydrated } from "@ca/db/flights";
import type { ActiveProcessingStatus, AircraftProcessingStatus } from "@ca/types/aircraft";
import type { AircraftFlightProcessing } from "@ca/types/flights";
import type { MaybeRefOrGetter } from "vue";

import { toValue } from "vue";

import {
	useMutation, useQuery, useQueryClient, 
} from "@tanstack/vue-query";

import { client } from "@/api";
import { useDateStore } from "@/stores/dates";

import { PROCESSING_RETRY, processingRefetchInterval } from "./processing";

export const useAircraftByTail = (tailNumber: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const tail = toValue(tailNumber);
		const resp = await client.api.v1.tails({ tail }).get();
		return resp.data?.aircraft ?? null;
	},
	enabled: () => Boolean(toValue(tailNumber)),
	queryKey: ["aircraft", tailNumber],
	refetchOnMount: false,
});

export const useAircraftProcessing = (aircraftId: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.admin.aircraft({
			aircraftId: toValue(aircraftId),
		}).processing.get();
		if (resp.error) {
			throw resp.error;
		}
		return (resp.data?.status ?? null) as null | AircraftProcessingStatus;
	},
	enabled: () => Boolean(toValue(aircraftId)),
	queryKey: ["aircraft-processing", aircraftId],
	refetchInterval: processingRefetchInterval,
	retry: PROCESSING_RETRY,
});

export const useActiveProcessing = (enabled: MaybeRefOrGetter<boolean>) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.admin.processing.aircraft.get();
		if (resp.error) {
			throw resp.error;
		}
		return (resp.data?.status ?? null) as ActiveProcessingStatus | null;
	},
	enabled: () => toValue(enabled),
	queryKey: ["active-processing"],
	refetchInterval: processingRefetchInterval,
	retry: PROCESSING_RETRY,
});

const getSinceUntil = () => {
	const dateStore = useDateStore();
	const since = dateStore.since.toISOString();
	const until = dateStore.until.toISOString();
	return { since, until };
};

const getForwardLookingSinceUntil = () => {
	const { since, until } = getSinceUntil();
	// If until is today, add 2 days to ensure we capture all flights
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	
	const untilDate = new Date(until);
	untilDate.setHours(0, 0, 0, 0);
	
	if (untilDate.getTime() === today.getTime()) {
		const adjustedUntil = new Date(until);
		adjustedUntil.setDate(adjustedUntil.getDate() + 2);
		return { since, until: adjustedUntil.toISOString() };
	}
	return { since, until };
};


export const useAircraftFlights = (aircraftId: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const { since, until } = getForwardLookingSinceUntil();
		const resp = await client.api.v1.aircraft({ aircraftId: toValue(aircraftId) }).flights.get({
			query: {
				since,
				until,
			},
		});
		return resp.data?.flights ?? [];
	},
	queryKey: [
		"flights",
		aircraftId,
		() => getForwardLookingSinceUntil().since,
		() => getForwardLookingSinceUntil().until,
	],
	refetchOnMount: false,
});


export const useAircraftFlightSummaries = (
	aircraftId: MaybeRefOrGetter<string>,
	window: MaybeRefOrGetter<{ since: string; until: string }>,
) => useQuery({
	queryFn: async () => {
		const { since, until } = toValue(window);
		const resp = await client.api.v1.aircraft({ aircraftId: toValue(aircraftId) }).flights.summary.get({
			query: {
				since,
				until,
			},
		});
		if (resp.error) {
			throw resp.error;
		}
		return resp.data as AircraftFlightSummaries;
	},
	enabled: () => Boolean(toValue(aircraftId)),
	queryKey: [
		"flight-summaries",
		aircraftId,
		() => toValue(window).since,
		() => toValue(window).until,
	],
	staleTime: 60_000,
});

export const aircraftFlightBatchQuery = (aircraftId: string, ids: string[], version: number) => ({
	queryFn: async () => {
		const resp = await client.api.v1.aircraft({ aircraftId }).flights.batch.get({
			query: { ids },
		});
		if (resp.error) {
			throw resp.error;
		}
		return {
			flights: resp.data.flights as FlightHydrated[],
			ids,
			version,
		};
	},
	queryKey: ["flight-batch", aircraftId, version, ...ids],
	refetchOnWindowFocus: false,
});

export const useAircraftFlightsProcessing = (aircraftId: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const { since, until } = getForwardLookingSinceUntil();
		const resp = await client.api.v1.aircraft({
			aircraftId: toValue(aircraftId),
		}).flights.processing.get({
			query: {
				since,
				until,
			},
		});
		return (resp.data?.processing ?? null) as AircraftFlightProcessing | null;
	},
	enabled: () => Boolean(toValue(aircraftId)),
	queryKey: [
		"flights-processing",
		aircraftId,
		() => getForwardLookingSinceUntil().since,
		() => getForwardLookingSinceUntil().until,
	],
	refetchInterval: processingRefetchInterval,
	retry: PROCESSING_RETRY,
});

export const useAircraftStats = (aircraftId: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const { since, until } = getSinceUntil();
		const resp = await client.api.v1.aircraft({ aircraftId: toValue(aircraftId) }).stats.get({
			query: {
				since,
				until,
			},
		});
		return resp.data?.stats ?? null;
	},
	queryKey: ["stats", aircraftId, () => getSinceUntil().since, () => getSinceUntil().until],
	refetchOnMount: false,
});

export const useAircraftTopMissions = (aircraftId: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const { since, until } = getSinceUntil();
		const resp = await client.api.v1.aircraft({ aircraftId: toValue(aircraftId) }).missions.get({
			query: {
				since,
				until,
			},
		});
		return resp.data?.missions ?? [];
	},
	queryKey: ["missions", aircraftId, () => getSinceUntil().since, () => getSinceUntil().until],
	refetchOnMount: false,
});

export const useAircraftSubmissions = (aircraftId: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.aircraft({
			aircraftId: toValue(aircraftId),
		}).submissions.get();
		return resp.data?.submissions ?? [];
	},
	queryKey: ["submissions", aircraftId],
	refetchOnMount: false,
});

export const useAircraftPolicies = (aircraftId: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.aircraft({
			aircraftId: toValue(aircraftId),
		}).policies.get();
		return resp.data?.policies ?? [];
	},
	queryKey: ["aircraft-policies", aircraftId],
	refetchOnMount: false,
});

export const useAircraftIncidents = (aircraftId: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.aircraft({
			aircraftId: toValue(aircraftId),
		}).incidents.get();
		return resp.data?.incidents ?? [];
	},
	queryKey: ["aircraft-incidents", aircraftId],
	refetchOnMount: false,
});

export const useUpdateAircraftConfig = (aircraftId: MaybeRefOrGetter<string>, key: MaybeRefOrGetter<string>, value: MaybeRefOrGetter<string | number>) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async () => {
			await client.api.v1.admin.aircraft({ aircraftId: toValue(aircraftId) }).config.put({
				key: toValue(key),
				value: toValue(value),
			});
			queryClient.invalidateQueries({ queryKey: ["stats", aircraftId] });
		},
	});
};

export const useDeleteAircraftFlightsByProvider = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({
			aircraftId,
			provider,
		}: {
			aircraftId: MaybeRefOrGetter<string>;
			provider: "adsbx" | "airsync" | "flightaware" | "fr24";
		}) => {
			const resolvedAircraftId = toValue(aircraftId);
			const resp = await client.api.v1.admin.aircraft({ aircraftId: resolvedAircraftId }).flights.provider({
				provider,
			}).delete();
			if (resp.status !== 200) {
				throw new Error((resp.error as { value?: { message?: string } })?.value?.message ?? "Failed to delete flights");
			}
			return {
				aircraftId: resolvedAircraftId,
				deletedCount: resp.data?.deletedCount ?? 0,
			};
		},
		onSuccess: ({ aircraftId }) => {
			queryClient.invalidateQueries({ queryKey: ["flights", aircraftId] });
			queryClient.invalidateQueries({ queryKey: ["flight-summaries", aircraftId] });
			queryClient.invalidateQueries({ queryKey: ["stats", aircraftId] });
			queryClient.invalidateQueries({ queryKey: ["missions", aircraftId] });
			queryClient.invalidateQueries({ queryKey: ["aircraft"] });
			queryClient.invalidateQueries({ queryKey: ["flight-id"] });
		},
	});
};
